import {
  decodeLbcd,
  encodeLbcd,
  type RadioChannelExtra,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const PAGE = 0x1000
const CHANNEL_PAGE_COUNT = 48
const CHANNEL_COUNT = 4000
const CHANNEL_SIZE = 0x30
const CHANNEL_PAGE0_COUNT = 84
const CHANNEL_PAGE_COUNT_SIZE = 85
const CONTACT_MAX = 800
const CONTACT_PER_PAGE = 170
const CONTACT_RECORD_SIZE = 0x18
const CONTACT_NAME_LENGTH = 16
const CONTACT_RECORD_TAG0 = 0x44
const CONTACT_INDEX_TAG = 0x0b
const RADIO_ID_TAG = 0x67
const RADIO_ID_MAX = 250
const RADIO_ID_NAME_LENGTH = 12
const RX_GROUP_TAG = 0x0f
const RX_GROUP_MAX = 32
const RX_GROUP_NAME_LENGTH = 11
const RX_GROUP_MEMBERS = 32
const RX_GROUP_SIZE = 0x6d
const SCAN_LIST_TAG = 0x11
const SCAN_LIST_MAX = 32
const SCAN_LIST_SIZE = 0x39
const SCAN_LIST_NAME_LENGTH = 11
const SCAN_LIST_MEMBERS = 16
const PRIVACY_TAG = 0x10
const PRIVACY_FIRST = 0x301
const PRIVACY_SIZE = 0x2c
const PRIVACY_COUNT = 32
const PRIVACY_NAME_LENGTH = 10
const ZONE_TAG0 = 0x5c
const ZONE_COUNT_MAX = 250
const ZONE_PER_PAGE = 28
const ZONE_SIZE = 0x91
const ZONE_MEMBERS = 64
const TX_CONTACT_TAG_LOW = 0x42
const TX_CONTACT_TAG_HIGH = 0x43
const MODEL_ID = 'DP570UV'
const TESTED_FIRMWARE = 'DM32.01.01.047'
const DEFAULT_CHANNEL_TAIL = Uint8Array.from([
  0x00, 0xff, 0xff, 0xff, 0xff, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
])

export const DM32UV_PAGE = PAGE
export const DM32UV_CHANNEL_COUNT = CHANNEL_COUNT
export const DM32UV_IMAGE_TAGS = [
  0x02, 0x03, 0x04, 0x06, 0x0a, 0x0b, 0x0f, 0x10, 0x11,
  ...Array.from({ length: 0x48 - 0x12 + 1 }, (_, index) => 0x12 + index),
  ...Array.from({ length: 0x67 - 0x5c + 1 }, (_, index) => 0x5c + index),
] as const
export const DM32UV_IMAGE_SIZE = DM32UV_IMAGE_TAGS.length * PAGE
export const DM32UV_WRITE_TAGS = [
  SCAN_LIST_TAG,
  ...Array.from({ length: 0x42 - 0x12 + 1 }, (_, index) => 0x12 + index),
  TX_CONTACT_TAG_LOW,
  TX_CONTACT_TAG_HIGH,
  ...Array.from({ length: 0x64 - 0x5c + 1 }, (_, index) => 0x5c + index),
] as const

type BaofengDM32UVSpec = {
  id: string
  model: string
  summary: string
}

export type BaofengDM32UVProfile = {
  protocol: 'baofeng-dm32uv'
  id: string
  vendor: 'Baofeng'
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 115200
  modes: readonly ['FM', 'NFM', 'DMR']
  channelMinimum: 1
  channelMaximum: number
  nameLength: 16
  memorySize: number
  powerLevels: readonly ['Low', 'Middle', 'High']
}

const SPECS: readonly BaofengDM32UVSpec[] = [
  {
    id: 'baofeng-dm-32uv',
    model: 'DM-32UV',
    summary:
      'Baofeng DMR handheld with analog and digital channels, zones, contacts, and RX groups.',
  },
]

export const BAOFENG_DM32UV_PROFILES: readonly BaofengDM32UVProfile[] =
  SPECS.map((spec) => ({
    protocol: 'baofeng-dm32uv',
    id: spec.id,
    vendor: 'Baofeng',
    model: spec.model,
    summary: spec.summary,
    tags: ['Experimental', 'Read + write', 'DMR', '4,000 channels'],
    baudRate: 115200,
    modes: ['FM', 'NFM', 'DMR'],
    channelMinimum: 1,
    channelMaximum: CHANNEL_COUNT,
    nameLength: 16,
    memorySize: DM32UV_IMAGE_SIZE,
    powerLevels: ['Low', 'Middle', 'High'],
  }))

export type DM32UVContact = {
  slot: number
  name: string
  dmrId: number
  callType: number
}

export type DM32UVDmrContext = {
  contacts: readonly DM32UVContact[]
  radioIds: readonly { index: number; name: string; dmrId: number }[]
  rxGroups: readonly { index: number; name: string }[]
  scanLists: readonly { index: number; name: string }[]
  privacyKeys: readonly { index: number; name: string }[]
}

function pageTagIndex(tag: number): number {
  const index = DM32UV_IMAGE_TAGS.indexOf(tag as (typeof DM32UV_IMAGE_TAGS)[number])
  if (index < 0) throw new Error(`DM-32UV image does not contain page tag 0x${tag.toString(16)}.`)
  return index
}

function pageOffset(tag: number): number {
  return pageTagIndex(tag) * PAGE
}

function pageBytes(image: Uint8Array, tag: number): Uint8Array {
  const start = pageOffset(tag)
  return image.subarray(start, start + PAGE)
}

function markPage(image: Uint8Array, tag: number): void {
  const start = pageOffset(tag)
  image[start + PAGE - 1] = tag
}

function decodeText(raw: Uint8Array): string {
  const bytes = raw.slice()
  let end = bytes.findIndex((value) => value === 0 || value === 0xff)
  if (end < 0) end = bytes.byteLength
  return new TextDecoder('latin1').decode(bytes.slice(0, end)).trim()
}

function encodeText(value: string, length: number): Uint8Array {
  const result = new Uint8Array(length)
  const text = value.trim().slice(0, length)
  for (let index = 0; index < text.length; index += 1) {
    const code = text.charCodeAt(index)
    result[index] = code >= 0x20 && code <= 0x7e ? code : 0x3f
  }
  return result
}

function readUint16(image: Uint8Array, offset: number): number {
  return (image[offset] ?? 0) | ((image[offset + 1] ?? 0) << 8)
}

function writeUint16(image: Uint8Array, offset: number, value: number): void {
  image[offset] = value & 0xff
  image[offset + 1] = (value >> 8) & 0xff
}

function readUint24(raw: Uint8Array, offset: number): number {
  return raw[offset] | (raw[offset + 1] << 8) | (raw[offset + 2] << 16)
}

function channelPageIndex(number: number): number {
  if (number <= CHANNEL_PAGE0_COUNT) return 0
  return 1 + Math.floor((number - CHANNEL_PAGE0_COUNT - 1) / CHANNEL_PAGE_COUNT_SIZE)
}

function channelPageTag(number: number): number {
  return 0x12 + channelPageIndex(number)
}

export function dm32uvChannelOffset(number: number): number {
  if (number < 1 || number > CHANNEL_COUNT) {
    throw new Error(`DM-32UV channel ${number} is outside 1-${CHANNEL_COUNT}.`)
  }
  if (number <= CHANNEL_PAGE0_COUNT) {
    return pageOffset(0x12) + 0x10 + (number - 1) * CHANNEL_SIZE
  }
  const page = channelPageIndex(number)
  const slot = (number - CHANNEL_PAGE0_COUNT - 1) % CHANNEL_PAGE_COUNT_SIZE
  return pageOffset(0x12 + page) + slot * CHANNEL_SIZE
}

function dm32uvChannelCount(image: Uint8Array): number {
  const header = pageBytes(image, 0x12)
  const count = readUint16(header, 0)
  return count > CHANNEL_COUNT ? 0 : count
}

function setDm32uvChannelCount(image: Uint8Array, count: number): void {
  const header = pageBytes(image, 0x12)
  writeUint16(header, 0, Math.max(0, Math.min(CHANNEL_COUNT, count)))
  markPage(image, 0x12)
}

function txContactLocation(number: number): { tag: number; offset: number } {
  if (number < 0x800) {
    return { tag: TX_CONTACT_TAG_LOW, offset: 2 * (number - 1) }
  }
  return { tag: TX_CONTACT_TAG_HIGH, offset: 2 * (number & 0x7ff) }
}

function readTxContact(image: Uint8Array, number: number): number {
  const location = txContactLocation(number)
  const page = pageBytes(image, location.tag)
  const high = page[location.offset]
  const low = page[location.offset + 1]
  const contact = ((high >> 4) << 8) | low
  return contact > CONTACT_MAX ? 0 : contact
}

function writeTxContact(image: Uint8Array, number: number, contact: number, digital: boolean): void {
  const location = txContactLocation(number)
  const page = pageBytes(image, location.tag)
  const previous = page[location.offset]
  const flags = previous === 0xff ? 0 : previous & 0x0e
  page[location.offset] = ((contact >> 8) << 4) | flags | (digital ? 1 : 0)
  page[location.offset + 1] = contact & 0xff
  markPage(image, location.tag)
}

export function readDM32UVContacts(image: Uint8Array): DM32UVContact[] {
  const index = pageBytes(image, CONTACT_INDEX_TAG)
  const contacts: DM32UVContact[] = []
  for (let slot = 1; slot <= CONTACT_MAX; slot += 1) {
    if (((index[0x10 + Math.floor((slot - 1) / 8)] ?? 0xff) >> ((slot - 1) % 8)) & 1) {
      continue
    }
    const page = CONTACT_RECORD_TAG0 + Math.floor((slot - 1) / CONTACT_PER_PAGE)
    const offset = ((slot - 1) % CONTACT_PER_PAGE) * CONTACT_RECORD_SIZE
    const record = pageBytes(image, page).subarray(offset, offset + CONTACT_RECORD_SIZE)
    const rawType = record[0x16]
    contacts.push({
      slot,
      name: decodeText(record.subarray(2, 2 + CONTACT_NAME_LENGTH)),
      dmrId: readUint24(record, 0x13),
      callType: rawType >= 3 && rawType <= 5 ? rawType - 3 : 0,
    })
  }
  return contacts
}

function readDm32uvRadioIds(image: Uint8Array) {
  const page = pageBytes(image, RADIO_ID_TAG)
  const count = Math.min(page[0] ?? 0, RADIO_ID_MAX)
  return Array.from({ length: count }, (_, index) => {
    const offset = 16 * (index + 1)
    return {
      index: index + 1,
      dmrId: readUint24(page, offset),
      name: decodeText(page.subarray(offset + 3, offset + 3 + RADIO_ID_NAME_LENGTH)),
    }
  })
}

function readDm32uvRxGroups(image: Uint8Array) {
  const page = pageBytes(image, RX_GROUP_TAG)
  const groups: { index: number; name: string }[] = []
  for (let index = 1; index <= RX_GROUP_MAX; index += 1) {
    if (page.slice(0, 4).every((value) => value === 0xff)) break
    if (!(((page[Math.floor((index - 1) / 8)] ?? 0) >> ((index - 1) % 8)) & 1)) {
      continue
    }
    const offset = RX_GROUP_SIZE * index - 0x5c
    groups.push({
      index,
      name: decodeText(page.subarray(offset, offset + RX_GROUP_NAME_LENGTH)),
    })
  }
  return groups
}

function readDm32uvScanLists(image: Uint8Array) {
  const page = pageBytes(image, SCAN_LIST_TAG)
  const count = Math.min(page[0] ?? 0, SCAN_LIST_MAX)
  return Array.from({ length: count }, (_, index) => {
    const offset = SCAN_LIST_SIZE * (index + 1) - 0x38
    return {
      index: index + 1,
      name: decodeText(page.subarray(offset, offset + SCAN_LIST_NAME_LENGTH)),
    }
  })
}

function readDm32uvPrivacyKeys(image: Uint8Array) {
  const page = pageBytes(image, PRIVACY_TAG)
  return Array.from({ length: PRIVACY_COUNT }, (_, index) => {
    const offset = PRIVACY_FIRST + index * PRIVACY_SIZE
    return {
      index: index + 1,
      name: decodeText(page.subarray(offset, offset + PRIVACY_NAME_LENGTH)),
    }
  }).filter((entry) => entry.name)
}

export function readDM32UVDmrContext(image: Uint8Array): DM32UVDmrContext {
  return {
    contacts: readDM32UVContacts(image),
    radioIds: readDm32uvRadioIds(image),
    rxGroups: readDm32uvRxGroups(image),
    scanLists: readDm32uvScanLists(image),
    privacyKeys: readDm32uvPrivacyKeys(image),
  }
}

function choiceValue(value: number, label: string): string {
  return `${value}: ${label}`
}

function selectedIndex(value: string | number | boolean | undefined): number {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.max(0, Math.trunc(value))
  const match = String(value ?? '').match(/^(\d+):/)
  return match ? Number(match[1]) : 0
}

function contactTypeLabel(callType: number): string {
  return callType === 1 ? 'Group' : callType === 2 ? 'All Call' : 'Private'
}

function contactChoices(context: DM32UVDmrContext): string[] {
  return [
    '0: None',
    ...context.contacts.map((contact) =>
      choiceValue(
        contact.slot,
        `${contact.name || 'Unnamed'} · ${contact.dmrId} · ${contactTypeLabel(contact.callType)}`,
      ),
    ),
  ]
}

function radioIdChoices(context: DM32UVDmrContext): string[] {
  return [
    '0: Default',
    ...context.radioIds.map((entry) =>
      choiceValue(entry.index, `${entry.name || 'Unnamed'} · ${entry.dmrId}`),
    ),
  ]
}

function namedChoices(
  noneLabel: string,
  entries: readonly { index: number; name: string }[],
): string[] {
  return [choiceValue(0, noneLabel), ...entries.map((entry) => choiceValue(entry.index, entry.name))]
}

function txAdmitChoices(digital: boolean): string[] {
  return digital
    ? ['Always', 'Channel Idle', 'Color Code Idle']
    : ['Allow TX', 'Channel Idle', 'Match CTC', 'Non Match CTC']
}

function extraValue(
  extras: readonly RadioChannelExtra[] | undefined,
  name: string,
): string | number | boolean | undefined {
  return extras?.find((extra) => extra.name === name)?.value
}

function extraBoolean(
  extras: readonly RadioChannelExtra[] | undefined,
  name: string,
  fallback: boolean,
): boolean {
  const value = extraValue(extras, name)
  return typeof value === 'boolean' ? value : fallback
}

function extraInteger(
  extras: readonly RadioChannelExtra[] | undefined,
  name: string,
  fallback: number,
): number {
  const value = Number(extraValue(extras, name))
  return Number.isFinite(value) ? value : fallback
}

function buildTxContactValue(contact: number, context: DM32UVDmrContext): string {
  const match = context.contacts.find((entry) => entry.slot === contact)
  return contact
    ? choiceValue(
        contact,
        match
          ? `${match.name || 'Unnamed'} · ${match.dmrId} · ${contactTypeLabel(match.callType)}`
          : `Contact ${contact}`,
      )
    : '0: None'
}

function buildRadioIdValue(index: number, context: DM32UVDmrContext): string {
  const match = context.radioIds.find((entry) => entry.index === index)
  return index
    ? choiceValue(index, match ? `${match.name || 'Unnamed'} · ${match.dmrId}` : `Radio ID ${index}`)
    : '0: Default'
}

function buildNamedValue(
  index: number,
  entries: readonly { index: number; name: string }[],
  fallback: string,
): string {
  const match = entries.find((entry) => entry.index === index)
  return index ? choiceValue(index, match?.name || fallback) : choiceValue(0, 'None')
}

function channelExtras(
  raw: Uint8Array,
  number: number,
  digital: boolean,
  context: DM32UVDmrContext,
  detail: { txContact: number; txAdmit: number },
): RadioChannelExtra[] {
  const dmrModes = ['DMR'] as const
  return [
    {
      name: 'colorCode',
      label: 'Color code',
      kind: 'integer',
      minimum: 0,
      maximum: 15,
      value: (raw[29] ?? 0) & 0x0f,
      visibleModes: dmrModes,
      documentation: 'DMR colour code used by this channel, from 0 through 15.',
    },
    {
      name: 'timeSlot',
      label: 'Time slot',
      kind: 'choice',
      choices: ['1', '2'],
      value: (((raw[29] ?? 0) >> 4) & 1) === 0 ? '1' : '2',
      visibleModes: dmrModes,
      documentation: 'DMR repeater time slot for this channel.',
    },
    {
      name: 'txContact',
      label: 'TX contact / talk group',
      kind: 'choice',
      choices: contactChoices(context),
      value: buildTxContactValue(detail.txContact, context),
      visibleModes: dmrModes,
      documentation: 'Talk group, private contact, or all-call contact used when transmitting.',
    },
    {
      name: 'radioId',
      label: 'Radio ID',
      kind: 'choice',
      choices: radioIdChoices(context),
      value: buildRadioIdValue(raw[43] ?? 0, context),
      visibleModes: dmrModes,
      documentation: 'DMR identity transmitted by this channel. Default uses the radio’s primary ID.',
    },
    {
      name: 'rxGroup',
      label: 'RX group list',
      kind: 'choice',
      choices: namedChoices('None', context.rxGroups),
      value: buildNamedValue((raw[31] ?? 0) & 0x3f, context.rxGroups, 'Group'),
      visibleModes: dmrModes,
      documentation: 'Defines which talk groups this channel receives.',
    },
    {
      name: 'encrypt',
      label: 'Encryption',
      kind: 'boolean',
      value: Boolean((raw[31] ?? 0) & 0x40),
      visibleModes: dmrModes,
      documentation: 'Enables the selected DMR privacy key for this channel.',
    },
    {
      name: 'privacy',
      label: 'Privacy key',
      kind: 'choice',
      choices: namedChoices('None', context.privacyKeys),
      value: buildNamedValue(raw[30] ?? 0, context.privacyKeys, 'Key'),
      visibleModes: dmrModes,
      documentation: 'DMR encryption key reference. Key values are preserved, not edited here.',
    },
    {
      name: 'scanList',
      label: 'Scan list',
      kind: 'choice',
      choices: namedChoices('None', context.scanLists),
      value: buildNamedValue((raw[25] ?? 0) & 0x3f, context.scanLists, 'Scan list'),
      documentation: 'Scan list assigned to this channel.',
    },
    {
      name: 'txAdmit',
      label: 'TX admit',
      kind: 'choice',
      choices: txAdmitChoices(digital),
      value:
        txAdmitChoices(digital)[
          Math.min((((raw[26] ?? 0) >> 4) & 0x07), txAdmitChoices(digital).length - 1)
        ],
      documentation: 'Controls when this channel is allowed to transmit.',
    },
    {
      name: 'squelch',
      label: 'Squelch level',
      kind: 'integer',
      minimum: 0,
      maximum: 9,
      value: ((raw[28] ?? 0) >> 4) & 0x0f,
    },
    {
      name: 'forbidTx',
      label: 'Forbid transmit',
      kind: 'boolean',
      value: Boolean((raw[24] ?? 0) & 0x08),
    },
    {
      name: 'forbidTalkaround',
      label: 'Forbid talkaround',
      kind: 'boolean',
      value: Boolean((raw[26] ?? 0) & 0x80),
    },
    {
      name: 'autoScan',
      label: 'Auto scan',
      kind: 'boolean',
      value: Boolean((raw[25] ?? 0) & 0x40),
    },
    {
      name: 'loneWorker',
      label: 'Lone worker',
      kind: 'boolean',
      value: Boolean((raw[24] ?? 0) & 0x01),
    },
  ]
}

function emptyExtras(context: DM32UVDmrContext): RadioChannelExtra[] {
  const raw = new Uint8Array(CHANNEL_SIZE)
  raw[29] = 0
  raw[31] = 0
  raw[43] = 0
  return channelExtras(raw, 1, false, context, { txContact: 0, txAdmit: 0 })
}

function emptyChannel(number: number, context: DM32UVDmrContext, extras?: RadioChannelExtra[]): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'FM',
    power: 'Low',
    skip: false,
    extras: extras ?? emptyExtras(context),
  }
}

function decodeDm32uvTone(raw: Uint8Array): Pick<
  WebChannel,
  'toneMode' | 'ctcss' | 'dtcs' | 'dtcsPolarity'
> {
  const value = readUint16(raw, 0)
  if (value === 0 || value === 0xffff) {
    return { toneMode: 'none', ctcss: null, dtcs: null, dtcsPolarity: 'N' }
  }
  if ((value & 0x8000) !== 0) {
    const digits = (value & 0x3fff).toString(16).padStart(4, '0')
    if (!/^\d{4}$/.test(digits)) {
      return { toneMode: 'none', ctcss: null, dtcs: null, dtcsPolarity: 'N' }
    }
    return {
      toneMode: 'dtcs',
      ctcss: null,
      dtcs: Number(digits.slice(-3)),
      dtcsPolarity: (value & 0x4000) !== 0 ? 'R' : 'N',
    }
  }
  const digits = value.toString(16).padStart(4, '0')
  if (!/^\d{4}$/.test(digits)) {
    return { toneMode: 'none', ctcss: null, dtcs: null, dtcsPolarity: 'N' }
  }
  return {
    toneMode: 'ctcss',
    ctcss: Number(digits) / 10,
    dtcs: null,
    dtcsPolarity: 'N',
  }
}

function encodeDm32uvTone(channel: WebChannel, target: Uint8Array): void {
  let value = 0xffff
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    value = Number(String(Math.round(channel.ctcss * 10)).padStart(4, '0'))
  } else if (channel.toneMode === 'dtcs' && channel.dtcs) {
    const digits = String(channel.dtcs).padStart(3, '0')
    value = 0x8000 | parseInt(digits, 16) | (channel.dtcsPolarity === 'R' ? 0x4000 : 0)
  }
  target[0] = value & 0xff
  target[1] = (value >> 8) & 0xff
}

export function parseDM32UVChannel(
  image: Uint8Array,
  profile: BaofengDM32UVProfile,
  number: number,
  localContext?: DM32UVDmrContext,
): WebChannel {
  const context = localContext ?? readDM32UVDmrContext(image)
  const offset = dm32uvChannelOffset(number)
  const raw = image.subarray(offset, offset + CHANNEL_SIZE)
  if (
    raw.byteLength !== CHANNEL_SIZE ||
    raw.subarray(16, 20).every((value) => value === 0xff || value === 0)
  ) {
    return emptyChannel(number, context)
  }
  const rx = decodeLbcd(raw.subarray(16, 20))
  const txBytes = raw.subarray(20, 24)
  const tx = txBytes.every((value) => value === 0xff)
    ? null
    : decodeLbcd(txBytes)
  if (!rx) return emptyChannel(number, context)
  const chType = (raw[24] >> 4) & 0x0f
  const digital = chType === 1 || chType === 3
  const txTone = decodeDm32uvTone(raw.subarray(35, 37))
  const rxTone = decodeDm32uvTone(raw.subarray(33, 35))
  const selected =
    txTone.toneMode !== 'none'
      ? txTone
      : rxTone.toneMode !== 'none'
        ? rxTone
        : txTone
  const powerIndex = (raw[24] >> 1) & 0x03
  return {
    number,
    empty: false,
    name: decodeText(raw.subarray(0, profile.nameLength)),
    rxFrequency: rx,
    txFrequency: tx,
    toneMode: digital ? 'none' : selected.toneMode,
    ctcss: digital ? null : selected.ctcss,
    dtcs: digital ? null : selected.dtcs,
    dtcsPolarity: digital ? 'N' : selected.dtcsPolarity,
    mode: digital ? 'DMR' : raw[25] & 0x80 ? 'FM' : 'NFM',
    power: profile.powerLevels[Math.min(powerIndex, 2)],
    skip: false,
    extras: channelExtras(raw, number, digital, context, {
      txContact: readTxContact(image, number),
      txAdmit: (raw[26] >> 4) & 0x07,
    }),
  }
}

export function parseDM32UVChannels(
  image: Uint8Array,
  profile: BaofengDM32UVProfile,
): WebChannel[] {
  if (image.byteLength !== profile.memorySize) {
    throw new Error(`DM-32UV images must be exactly ${profile.memorySize} bytes.`)
  }
  const context = readDM32UVDmrContext(image)
  const sharedEmptyExtras = emptyExtras(context)
  const count = dm32uvChannelCount(image)
  const slots = Math.min(profile.channelMaximum, Math.max(100, count + 50))
  return Array.from({ length: slots }, (_, index) =>
    parseDM32UVChannel(image, profile, index + 1, context) ?? emptyChannel(index + 1, context, sharedEmptyExtras),
  )
}

function removeChannelFromZones(image: Uint8Array, number: number): void {
  const firstPage = pageBytes(image, ZONE_TAG0)
  const zoneCount = Math.min(firstPage[0] ?? 0, ZONE_COUNT_MAX)
  for (let zone = 1; zone <= zoneCount; zone += 1) {
    const pageIndex = Math.floor((zone - 1) / ZONE_PER_PAGE)
    const slot = (zone - 1) % ZONE_PER_PAGE
    const tag = ZONE_TAG0 + pageIndex
    const page = pageBytes(image, tag)
    const offset = (pageIndex === 0 ? 0x10 : 0) + slot * ZONE_SIZE
    const record = page.subarray(offset, offset + ZONE_SIZE)
    const memberCount = Math.min(record[16] ?? 0, ZONE_MEMBERS)
    const members = Array.from(
      { length: memberCount },
      (_, index) => readUint16(record, 17 + 2 * index),
    ).filter((member) => member !== number)
    if (members.length === memberCount) continue
    writeUint16(record, 17 + 2 * members.length, 0)
    for (let index = 0; index < members.length; index += 1) {
      writeUint16(record, 17 + 2 * index, members[index])
    }
    record.fill(0, 17 + 2 * members.length, 17 + 2 * memberCount)
    record[16] = members.length
    markPage(image, tag)
  }
}

function removeChannelFromScanLists(image: Uint8Array, number: number): void {
  const page = pageBytes(image, SCAN_LIST_TAG)
  const count = Math.min(page[0] ?? 0, SCAN_LIST_MAX)
  for (let list = 1; list <= count; list += 1) {
    const offset = SCAN_LIST_SIZE * list - 0x38
    const record = page.subarray(offset, offset + SCAN_LIST_SIZE)
    const memberCount = Math.min(record[0x0b] ?? 0, SCAN_LIST_MEMBERS)
    const members = Array.from(
      { length: memberCount },
      (_, index) => readUint16(record, 0x18 + 2 * index),
    ).filter((member) => member !== number)
    if (members.length === memberCount) continue
    record[0x0b] = members.length
    for (let index = 0; index < SCAN_LIST_MEMBERS; index += 1) {
      writeUint16(record, 0x18 + 2 * index, members[index] ?? 0)
    }
    markPage(image, SCAN_LIST_TAG)
  }
}

function removeReferencesToChannel(image: Uint8Array, number: number): void {
  removeChannelFromZones(image, number)
  removeChannelFromScanLists(image, number)
}

function updateChannelCount(image: Uint8Array, number: number, empty: boolean): void {
  const current = dm32uvChannelCount(image)
  if (!empty) {
    if (number > current) setDm32uvChannelCount(image, number)
    return
  }
  if (number !== current) return
  let next = current - 1
  while (next > 0) {
    const offset = dm32uvChannelOffset(next)
    const raw = image.subarray(offset + 16, offset + 20)
    if (!raw.every((value) => value === 0xff || value === 0)) break
    next -= 1
  }
  setDm32uvChannelCount(image, next)
}

export function applyDM32UVChannel(
  image: Uint8Array,
  profile: BaofengDM32UVProfile,
  channel: WebChannel,
): void {
  const offset = dm32uvChannelOffset(channel.number)
  const raw = image.subarray(offset, offset + CHANNEL_SIZE)
  const tag = channelPageTag(channel.number)
  const previousWasEmpty = raw
    .subarray(16, 20)
    .every((value) => value === 0xff || value === 0)
  if (channel.empty) {
    raw.fill(0xff)
    writeTxContact(image, channel.number, 0, false)
    removeReferencesToChannel(image, channel.number)
    updateChannelCount(image, channel.number, true)
    markPage(image, tag)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  if (previousWasEmpty) {
    raw.fill(0xff)
    raw.fill(0, 16, 24)
    raw.set(DEFAULT_CHANNEL_TAIL, 24)
  }
  raw.fill(0, 0, 16)
  raw.set(encodeText(channel.name, profile.nameLength), 0)
  encodeLbcd(channel.rxFrequency, raw.subarray(16, 20))
  if (channel.txFrequency) encodeLbcd(channel.txFrequency, raw.subarray(20, 24))
  else raw.fill(0xff, 20, 24)

  const digital = channel.mode === 'DMR'
  let channelType = (raw[24] >> 4) & 0x0f
  if (digital && channelType !== 1 && channelType !== 3) {
    channelType = channelType === 2 ? 3 : 1
  } else if (!digital && channelType !== 0 && channelType !== 2) {
    channelType = channelType === 3 ? 2 : 0
  }
  const powerIndex = Math.max(0, profile.powerLevels.indexOf(channel.power as 'Low' | 'Middle' | 'High'))
  raw[24] =
    ((channelType & 0x0f) << 4) |
    (extraBoolean(channel.extras, 'forbidTx', Boolean(raw[24] & 0x08)) ? 0x08 : 0) |
    ((powerIndex & 0x03) << 1) |
    (extraBoolean(channel.extras, 'loneWorker', Boolean(raw[24] & 0x01)) ? 0x01 : 0)
  raw[25] =
    (channel.mode === 'FM' ? 0x80 : 0) |
    (extraBoolean(channel.extras, 'autoScan', Boolean(raw[25] & 0x40)) ? 0x40 : 0) |
    (selectedIndex(extraValue(channel.extras, 'scanList')) & 0x3f)

  if (!digital) {
    const txTone = new Uint8Array(2)
    const rxTone = new Uint8Array(2)
    encodeDm32uvTone(channel, txTone)
    encodeDm32uvTone(channel, rxTone)
    raw.set(rxTone, 33)
    raw.set(txTone, 35)
  }

  const admitOptions = txAdmitChoices(digital)
  const admittance = admitOptions.indexOf(String(extraValue(channel.extras, 'txAdmit') ?? ''))
  raw[26] =
    (extraBoolean(channel.extras, 'forbidTalkaround', Boolean(raw[26] & 0x80)) ? 0x80 : 0) |
    (((admittance >= 0 ? admittance : (raw[26] >> 4) & 0x07) & 0x07) << 4) |
    (raw[26] & 0x0f)
  raw[28] =
    (raw[28] & 0x0f) |
    ((Math.max(0, Math.min(9, extraInteger(channel.extras, 'squelch', (raw[28] >> 4) & 0x0f))) &
      0x0f) <<
      4)
  const timeSlot = String(extraValue(channel.extras, 'timeSlot') ?? '1') === '2' ? 1 : 0
  raw[29] =
    (raw[29] & 0xe0) |
    (timeSlot << 4) |
    (Math.max(0, Math.min(15, extraInteger(channel.extras, 'colorCode', 0))) & 0x0f)
  raw[30] = selectedIndex(extraValue(channel.extras, 'privacy')) & 0xff
  raw[31] =
    (raw[31] & 0x80) |
    (extraBoolean(channel.extras, 'encrypt', false) ? 0x40 : 0) |
    (selectedIndex(extraValue(channel.extras, 'rxGroup')) & 0x3f)
  raw[43] = selectedIndex(extraValue(channel.extras, 'radioId')) & 0xff
  markPage(image, tag)

  writeTxContact(
    image,
    channel.number,
    selectedIndex(extraValue(channel.extras, 'txContact')),
    digital,
  )
  updateChannelCount(image, channel.number, false)
}

async function send(transport: RadioTransport, command: Uint8Array): Promise<void> {
  await transport.discardInput()
  await transport.write(command)
}

async function xfer(
  transport: RadioTransport,
  command: Uint8Array,
  responseLength: number,
  timeoutMs = 1000,
): Promise<Uint8Array> {
  await send(transport, command)
  return transport.readExact(responseLength, timeoutMs)
}

async function queryV(transport: RadioTransport, command: Uint8Array): Promise<Uint8Array> {
  await send(transport, command)
  const header = await transport.readExact(3, 1000)
  if (header[0] !== 0x56) throw new Error('The DM-32UV returned an invalid information response.')
  const length = header[2]
  const body = length ? await transport.readExact(length, 1500) : new Uint8Array()
  return body
}

async function identifyDM32UV(
  transport: RadioTransport,
  writing: boolean,
): Promise<{ firmware: string; start: number; end: number }> {
  let model: Uint8Array | null = null
  for (let attempt = 0; attempt < 5; attempt += 1) {
    await send(transport, new TextEncoder().encode('PSEARCH'))
    const response = await transport.readExact(8, 800)
    if (response[0] === ACK) {
      model = response.slice(1)
      break
    }
  }
  if (!model) {
    throw new Error(
      'The radio did not respond. Confirm it is powered on and connected with a working programming cable.',
    )
  }
  const modelText = new TextDecoder().decode(model)
  if (modelText !== MODEL_ID) {
    throw new Error(`This radio identifies as ${JSON.stringify(modelText)}, not a Baofeng DM-32UV.`)
  }
  const passwordState = await xfer(transport, new TextEncoder().encode('PASSSTA'), 3)
  if (passwordState[0] !== 0x50) throw new Error('The DM-32UV returned an invalid password-state response.')
  if (passwordState[2] === 0xa5) {
    throw new Error('The radio has a read password. Remove it with the Baofeng software before using Web Serial.')
  }
  if (writing && passwordState[1] === 0xa5) {
    throw new Error('The radio has a write password. Remove it with the Baofeng software before using Web Serial.')
  }
  if ((await xfer(transport, new TextEncoder().encode('SYSINFO'), 1))[0] !== ACK) {
    throw new Error('The DM-32UV refused the system-information request.')
  }
  await queryV(transport, Uint8Array.from([0x56, 0x00, 0x00, 0x40, 0x0d]))
  const info = new Map<number, Uint8Array>()
  for (let index = 1; index <= 16; index += 1) {
    if (index === 12) continue
    info.set(index, await queryV(transport, Uint8Array.from([0x56, 0, 0, 0, index])))
  }
  const range = info.get(10)
  if (!range || range.byteLength < 8) throw new Error('The radio did not report a valid codeplug range.')
  const start =
    range[0] | (range[1] << 8) | (range[2] << 16) | (range[3] << 24)
  const end =
    range[4] | (range[5] << 8) | (range[6] << 16) | (range[7] << 24)
  if (start >= end || (start | end) >>> 24) {
    throw new Error('The radio reported an invalid codeplug range.')
  }
  const firmware = new TextDecoder().decode(info.get(1) ?? new Uint8Array())
  return { firmware, start, end }
}

async function enterProgram(transport: RadioTransport): Promise<void> {
  if ((await xfer(transport, Uint8Array.from([0x47, 0, 0, 0, 0, 1]), 0x106, 2500))[0] !== 0x53) {
    throw new Error('The DM-32UV rejected the initial programming-mode request.')
  }
  await send(transport, Uint8Array.from([0xff, 0xff, 0xff, 0xff, 0x0c]))
  if ((await xfer(transport, new TextEncoder().encode('PROGRAM'), 1, 2000))[0] !== ACK) {
    throw new Error('The DM-32UV refused programming mode.')
  }
  await xfer(transport, Uint8Array.from([0x02]), 8, 2000)
  if ((await xfer(transport, Uint8Array.from([0x06]), 1, 1500))[0] !== ACK) {
    throw new Error('The DM-32UV did not acknowledge the programming session.')
  }
}

async function readBlock(
  transport: RadioTransport,
  address: number,
  length: number,
  timeoutMs = 6000,
): Promise<Uint8Array> {
  const command = Uint8Array.from([
    0x52,
    address & 0xff,
    (address >> 8) & 0xff,
    (address >> 16) & 0xff,
    length & 0xff,
    (length >> 8) & 0xff,
  ])
  await send(transport, command)
  const response = await transport.readExact(length + 6, timeoutMs)
  if (
    response[0] !== 0x57 ||
    response[1] !== command[1] ||
    response[2] !== command[2] ||
    response[3] !== command[3] ||
    response[4] !== command[4] ||
    response[5] !== command[5]
  ) {
    throw new Error(`The DM-32UV returned a corrupt read at 0x${address.toString(16)}.`)
  }
  return response.slice(6)
}

async function scanPages(
  transport: RadioTransport,
  start: number,
  end: number,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ addresses: Map<number, number[]>; free: number[]; scanned: number }> {
  const addresses = new Map<number, number[]>()
  const free: number[] = []
  let scanned = 0
  for (let address = start; address + PAGE - 1 <= end; address += PAGE) {
    const tag = (await readBlock(transport, address + PAGE - 1, 1, 1200))[0]
    if (tag >= 0x80 && tag < 0xff) {
      throw new Error(
        `The radio returned an implausible page tag at 0x${address.toString(16)}. ` +
          'A CH340 cable may be corrupting data; use an FTDI or CP2102 cable.',
      )
    }
    if (tag === 0xff) {
      free.push(address)
    } else {
      const matches = addresses.get(tag) ?? []
      matches.push(address)
      addresses.set(tag, matches)
    }
    scanned += 1
    onProgress?.({
      current: scanned,
      total: scanned + DM32UV_IMAGE_TAGS.length,
      label: 'Scanning DM-32UV flash pages',
    })
  }
  return { addresses, free, scanned }
}

export async function readBaofengDM32UVRadioWithTransport(
  profile: BaofengDM32UVProfile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  const radio = await identifyDM32UV(transport, false)
  await enterProgram(transport)
  const scan = await scanPages(transport, radio.start, radio.end, onProgress)
  const image = new Uint8Array(profile.memorySize).fill(0xff)
  for (let index = 0; index < DM32UV_IMAGE_TAGS.length; index += 1) {
    const tag = DM32UV_IMAGE_TAGS[index]
    const address = scan.addresses.get(tag)?.[0]
    if (address !== undefined) {
      image.set(await readBlock(transport, address, PAGE), index * PAGE)
    }
    onProgress?.({
      current: scan.scanned + index + 1,
      total: scan.scanned + DM32UV_IMAGE_TAGS.length,
      label: 'Reading DMR channels, contacts, and radio lists',
    })
  }
  const firmware = radio.firmware === TESTED_FIRMWARE ? `firmware ${radio.firmware}` : `firmware ${radio.firmware} (unverified)`
  return { image, reportedModel: `DM-32UV · ${firmware}` }
}

async function writePage(
  transport: RadioTransport,
  address: number,
  data: Uint8Array,
): Promise<void> {
  if (data.byteLength !== PAGE) throw new Error('DM-32UV page writes must be exactly 4096 bytes.')
  const command = new Uint8Array(6 + PAGE)
  command.set([
    0x57,
    address & 0xff,
    (address >> 8) & 0xff,
    (address >> 16) & 0xff,
    PAGE & 0xff,
    (PAGE >> 8) & 0xff,
  ])
  command.set(data, 6)
  await send(transport, command)
  const ack = await transport.readExact(1, 6000)
  const readBack = await readBlock(transport, address, PAGE, 7000)
  if (ack[0] !== ACK) {
    throw new Error(
      `The radio did not acknowledge the page at 0x${address.toString(16)}. ` +
        'Upload again, or restore the backup saved before this upload.',
    )
  }
  if (readBack.some((value, index) => value !== data[index])) {
    throw new Error(
      `The page at 0x${address.toString(16)} did not read back as written. ` +
        'Upload again, or restore the backup saved before this upload.',
    )
  }
}

export async function writeBaofengDM32UVRadioWithTransport(
  profile: BaofengDM32UVProfile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength !== profile.memorySize) {
    throw new Error(`DM-32UV images must be exactly ${profile.memorySize} bytes.`)
  }
  const radio = await identifyDM32UV(transport, true)
  await enterProgram(transport)
  const scan = await scanPages(transport, radio.start, radio.end, onProgress)
  const duplicate = DM32UV_WRITE_TAGS.find((tag) => (scan.addresses.get(tag)?.length ?? 0) > 1)
  if (duplicate !== undefined) {
    throw new Error(
      `The radio has multiple flash pages tagged 0x${duplicate.toString(16)}. ` +
        'Do not upload until the radio has consolidated its codeplug pages.',
    )
  }

  let written = 0
  for (let index = 0; index < DM32UV_WRITE_TAGS.length; index += 1) {
    const tag = DM32UV_WRITE_TAGS[index]
    const slot = image.slice(pageOffset(tag), pageOffset(tag) + PAGE)
    const hasData = !slot.subarray(0, PAGE - 1).every((value) => value === 0xff)
    const existing = scan.addresses.get(tag)?.[0]
    if (!hasData && !(slot[PAGE - 1] === tag && existing !== undefined)) {
      onProgress?.({
        current: scan.scanned + index + 1,
        total: scan.scanned + DM32UV_WRITE_TAGS.length,
        label: 'Checking DM-32UV flash pages',
      })
      continue
    }
    const desired = slot.slice()
    desired[PAGE - 1] = tag
    let address = existing
    if (address !== undefined) {
      const current = await readBlock(transport, address, PAGE, 7000)
      if (!current.some((value, offset) => value !== desired[offset])) {
        onProgress?.({
          current: scan.scanned + index + 1,
          total: scan.scanned + DM32UV_WRITE_TAGS.length,
          label: 'Checking DM-32UV flash pages',
        })
        continue
      }
    } else {
      address = scan.free.shift()
      if (address === undefined) throw new Error('The radio has no free flash page left for this edit.')
    }
    await writePage(transport, address, desired)
    written += 1
    onProgress?.({
      current: scan.scanned + index + 1,
      total: scan.scanned + DM32UV_WRITE_TAGS.length,
      label: 'Writing and verifying DMR channels',
    })
  }
  if (written === 0) {
    onProgress?.({
      current: scan.scanned + DM32UV_WRITE_TAGS.length,
      total: scan.scanned + DM32UV_WRITE_TAGS.length,
      label: 'No changed DM-32UV flash pages',
    })
  }
}
